import { useState, useEffect, useRef } from 'react';

import TopNavBar from '../components/TopNavBar';
import SideNavBar from '../components/SideNavBar';

import Refreshing from '../components/Refreshing';

import * as logger from '../logger';
import * as apiComms from '../apiComms';

import * as dayjs from 'dayjs'

import { IoMdSettings } from "react-icons/io";
import { FaTrashAlt } from "react-icons/fa";
import useIsMobile, { DESKTOP_CONTENT_MARGIN_LEFT, MOBILE_CONTENT_MARGIN_LEFT } from '../hooks/useIsMobile';

const PROG = 'Users.js';
const DEFAULT_ORDER_BY = 'createdondesc';

const Users = (props) => {

  const [showInfoBox, setShowInfoBox] = useState(true);  
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [isMoreUsersToLoad, setIsMoreUsersToLoad] = useState(false);
  const [allUsersCount, setAllUsersCount] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const loadMoreTriggerRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    refreshUsers();
  }, [orderBy, debouncedSearchText]);

  useEffect(() => {
    refreshAllUsersCount();
  }, []);

  useEffect(() => {
    const trimmedSearchText = searchText.trim();

    const timeoutId = setTimeout(() => {
      if (trimmedSearchText.length >= 3) {
        setDebouncedSearchText(trimmedSearchText);
      }
      else {
        setDebouncedSearchText('');
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchText]);

  useEffect(() => {
    if (isLoading || isMoreUsersToLoad || nextPageToken == null || !loadMoreTriggerRef.current) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        loadMoreUsers();
      }
    }, {
      root: null,
      rootMargin: '300px',
      threshold: 0
    });

    observer.observe(loadMoreTriggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isLoading, isMoreUsersToLoad, nextPageToken, users]);

  const refreshAllUsersCount = async () => {
    const FUNCTION = 'refreshAllUsersCount';

    setAllUsersCount(null);

    try {
      const _res = await apiComms.getAllUserCount();
      logger.debug('Response from apiComms.getAllUserCount', { PROG, FUNCTION, _res });
      if (!_res.isErr) {
        const _count = _res.response.count;
        setAllUsersCount(_count);
      }
    }
    catch (e) {
      logger.warn('Exception Occured', { PROG, FUNCTION });
      console.log('Exception: '+e+', '+e.stack);
    }
  };

  const refreshUsers = async () => {
    setIsLoading(true);
    setUsers([]);
    setNextPageToken(null);
    await _loadUsersPage(null, true);
    setIsLoading(false);
  };

  const loadMoreUsers = async () => {
    if (isLoading || isMoreUsersToLoad || isLoadingMoreRef.current || nextPageToken == null) {
      return;
    }

    isLoadingMoreRef.current = true;
    setIsMoreUsersToLoad(true);
    try {
      await _loadUsersPage(nextPageToken, false);
    }
    finally {
      setIsMoreUsersToLoad(false);
      isLoadingMoreRef.current = false;
    }
  };

  const _loadUsersPage = async (_nextPageToken, _replaceExistingUsers) => {
    const _usersRes = await apiComms.getUsers(orderBy, _nextPageToken, debouncedSearchText);
    console.log('RES: '+_usersRes);
    if (!_usersRes || _usersRes.isErr) {
      if (_usersRes) {
        //alert('Sorry an error occured: '+_usersRes.errMsg);
        console.log('Network Error Occured: '+_usersRes.errMsg);
      }
      else {
        //alert('Sorry an Network error occured');
        console.log('Network Error Occured');
      }
    }
    else {
      let _users = _usersRes.response.users;
      console.log('******** USER COUNT: '+_users.length);
      _enrichUsers(_users);
      _setAdditionalDataNotLoadedFlagOnUsers(_users);
      setNextPageToken(_usersRes.response.nextPageToken ?? null);
      setUsers((prevUsers) => {
        if (_replaceExistingUsers) {
          return _users;
        }
        return [...prevUsers, ..._users];
      });
      _loadAdditionalUserData(_users);
    }
  };

  const _enrichUsers = (_users) => {
    const FUNCTION = '_enrichUsers';

    _users.forEach((_user) => {
      _enrichUser(_user);
    });
  };

  const _enrichUser = (_user) => {
    const FUNCTION = '_enrichUser';
    
    const createdOnEpoch = _user.createdOn;
    const createdOnDayJs = dayjs(createdOnEpoch * 1000);
    const createdOnReadable = createdOnDayJs.format('DD/MMM/YYYY');

    let _liveCollectorCardCount = 0;
    let _closedCollectorCardCount = 0;
    let _totalStampsEver = 0;

    if (_user.hasOwnProperty('live_collectorcards')) {
      _liveCollectorCardCount = _user.live_collectorcards.length;
      for (const collectorCard of _user.live_collectorcards) {
        if (collectorCard.hasOwnProperty('transactionIds')) {
          const transactionIds = collectorCard.transactionIds;
          _totalStampsEver += transactionIds.length;
        }
      }
    }
    if (_user.hasOwnProperty('closed_collectorcards')) {
      _closedCollectorCardCount = _user.closed_collectorcards.length;
      for (const collectorCard of _user.closed_collectorcards) {
        if (collectorCard.hasOwnProperty('transactionIds')) {
          const transactionIds = collectorCard.transactionIds;
          _totalStampsEver += transactionIds.length;
        }
      }
    }

    _user.enrichedData = {
      createdOn : createdOnReadable,
      liveCollectorCardsCount : _liveCollectorCardCount,
      closedCollectorCardsCount : _closedCollectorCardCount,
      totalStampsEverCount : _totalStampsEver
    };
    //logger.debug('updated user record', { PROG, FUNCTION, _user });
  };

  const _setAdditionalDataNotLoadedFlagOnUsers = (_users) => {
    const FUNCTION = '_setAdditionalDataNotLoadedFlagOnUsers';

    _users.forEach((_user) => {
      _user.isAdditionalDataLoaded = false;
    });
  };

  const _loadAdditionalUserData = async (_users) => {
    const FUNCTION = '_loadAdditionalUserData';

    await Promise.allSettled(_users.map(async (_user) => {
      let _meridianCode = null;
      if (_user.hasOwnProperty('meridian_code') &&
          _user.meridian_code &&
          _user.meridian_code.trim() != '') {
        _meridianCode = _user.meridian_code;
      }
      else {
        if (_user.hasOwnProperty('meridian_accountKey') &&
            _user.meridian_accountKey &&
            _user.meridian_accountKey.trim() != '') {
          _meridianCode = _user.meridian_accountKey;
        }                            
        else {
          _meridianCode = '';
        }
      }
      const _email = _user.email;

      const _additionalData = await apiComms.getUserStats(_meridianCode);
      if (!_additionalData || _additionalData.isErr) {
        logger.warn('Error loading user stats', { PROG, FUNCTION, _additionalData });
        // Error loading additional data
        _user.additionalData = {};
        _user.isAdditionalDataLoaded = true;
      }
      else {
        _user.additionalData = _additionalData.response.stats;
        _user.isAdditionalDataLoaded = true;
      }
      setUsers(prevItems => prevItems.map((userItem) =>
        userItem.email === _user.email ? _user : userItem
      ));
    }));
  };

  const _toggleSignedUpSort = () => {
    setOrderBy((prevOrderBy) => prevOrderBy === 'createdondesc' ? 'createdonasc' : 'createdondesc');
  };

  const _toggleEmailSort = () => {
    setOrderBy((prevOrderBy) => prevOrderBy === 'emailasc' ? 'emaildesc' : 'emailasc');
  };

  const _toggleFirstNameSort = () => {
    setOrderBy((prevOrderBy) => prevOrderBy === 'firstnameasc' ? 'firstnamedesc' : 'firstnameasc');
  };

  const _toggleSurnameSort = () => {
    setOrderBy((prevOrderBy) => prevOrderBy === 'surnameasc' ? 'surnamedesc' : 'surnameasc');
  };

  const _toggleCountySort = () => {
    setOrderBy((prevOrderBy) => prevOrderBy === 'countyasc' ? 'countydesc' : 'countyasc');
  };

  const _toggleAccountIdSort = () => {
    setOrderBy((prevOrderBy) => prevOrderBy === 'accountidasc' ? 'accountiddesc' : 'accountidasc');
  };

  const _getSortArrow = (_ascValue, _descValue) => {
    if (orderBy === _ascValue) {
      return {
        icon: '↑',
        color: '#212529'
      };
    }
    if (orderBy === _descValue) {
      return {
        icon: '↓',
        color: '#212529'
      };
    }
    return {
      icon: '↓',
      color: '#adb5bd'
    };
  };

  const _handleUserRowClick = (accountId) => {
    if (!accountId || accountId.trim() === '') {
      return;
    }

    props.handleUpdate({ updateType : 'navigate', route : '/users-users/'+encodeURIComponent(accountId) });
  };
        
      
    
    

      

  return (
    <>
      <TopNavBar handleUpdate = { props.handleUpdate } activeRoute = { props.activeRoute } />
      <SideNavBar handleUpdate = { props.handleUpdate } activeRoute = { props.activeRoute } />
      <div style={{ marginLeft: isMobile ? MOBILE_CONTENT_MARGIN_LEFT : DESKTOP_CONTENT_MARGIN_LEFT, marginRight : 10, marginTop : 0, paddingTop : 45  }}>
        { allUsersCount === null ? (
          <h1 style={{ fontSize: 18, fontWeight : 'bold', paddingTop : 20 }}>Users & Engagement</h1>
        ) : (
          <h1 style={{ fontSize: 18, fontWeight : 'bold', paddingTop : 20 }}>Users & Engagement ({ allUsersCount } users)</h1>
        ) }

        { showInfoBox ? (
          <div style={{ marginTop : 20 }} className="alert alert-primary lert-dismissible fade show" role="alert">
            <h4 className="alert-heading">About Users</h4>
            <p>This is the current database of digital Loyalty App users.</p>
            <button style={{ position : 'absolute', top : 10, right : 10 }} type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close" onClick={ () => { setShowInfoBox(false); }} onPress={ () => { setShowInfoBox(false); }}></button>
          </div>
        ) : null }

        <div style={{ marginTop : 20, marginBottom : 15, maxWidth : 460 }}>
          <input
            type='text'
            className='form-control'
            placeholder='Search Account ID, First Name, Last Name, Email or County'
            value={ searchText }
            onChange={ (e) => { setSearchText(e.target.value); } }
          />
          <div style={{ fontSize : 12, color : '#6c757d', marginTop : 6 }}>
            Search starts after 3 characters and a 500ms pause.
          </div>
        </div>

        { isLoading ? (
          <Refreshing message='Loading users...' />
        ) : null }

        { !isLoading ? (
          <div style={{ flex : 1, width : '100%', overflowX : 'auto', overflowY : 'auto', whiteSpace : 'wrap', marginLeft : 0 }}>
            <table className='table' style={{ minWidth : '1200px' }}>
              <thead>
                <tr>
                  <th style={{ whiteSpace : 'nowrap' }}>
                    {(() => {
                      const accountIdSortArrow = _getSortArrow('accountidasc', 'accountiddesc');
                      return (
                        <button
                          type='button'
                          onClick={ _toggleAccountIdSort }
                          style={{
                            background : 'none',
                            border : 'none',
                            padding : 0,
                            font : 'inherit',
                            fontWeight : 'inherit',
                            cursor : 'pointer',
                            whiteSpace : 'nowrap'
                          }}
                        >
                          Account ID <span style={{ color : accountIdSortArrow.color }}>{ accountIdSortArrow.icon }</span>
                        </button>
                      );
                    })()}
                  </th>
	                  <th style={{ whiteSpace : 'nowrap' }}>
                    {(() => {
                      const firstNameSortArrow = _getSortArrow('firstnameasc', 'firstnamedesc');
                      return (
	                    <button
	                      type='button'
	                      onClick={ _toggleFirstNameSort }
	                      style={{
	                        background : 'none',
	                        border : 'none',
	                        padding : 0,
	                        font : 'inherit',
	                        fontWeight : 'inherit',
	                        cursor : 'pointer',
	                        whiteSpace : 'nowrap'
	                      }}
	                    >
	                      First Name <span style={{ color : firstNameSortArrow.color }}>{ firstNameSortArrow.icon }</span>
	                    </button>
                      );
                    })()}
                  </th>
	                  <th style={{ whiteSpace : 'nowrap' }}>
                    {(() => {
                      const surnameSortArrow = _getSortArrow('surnameasc', 'surnamedesc');
                      return (
	                    <button
	                      type='button'
	                      onClick={ _toggleSurnameSort }
	                      style={{
	                        background : 'none',
	                        border : 'none',
	                        padding : 0,
	                        font : 'inherit',
	                        fontWeight : 'inherit',
	                        cursor : 'pointer',
	                        whiteSpace : 'nowrap'
	                      }}
	                    >
	                      Last Name <span style={{ color : surnameSortArrow.color }}>{ surnameSortArrow.icon }</span>
	                    </button>
                      );
                    })()}
                  </th>
	                  <th style={{ whiteSpace : 'nowrap' }}>
                    {(() => {
                      const emailSortArrow = _getSortArrow('emailasc', 'emaildesc');
                      return (
	                    <button
	                      type='button'
	                      onClick={ _toggleEmailSort }
	                      style={{
	                        background : 'none',
                        border : 'none',
                        padding : 0,
                        font : 'inherit',
                        fontWeight : 'inherit',
	                        cursor : 'pointer',
	                        whiteSpace : 'nowrap'
	                      }}
	                    >
	                      Email <span style={{ color : emailSortArrow.color }}>{ emailSortArrow.icon }</span>
	                    </button>
                      );
                    })()}
	                  </th>
	                  <th style={{ whiteSpace : 'nowrap' }}>
                    {(() => {
                      const countySortArrow = _getSortArrow('countyasc', 'countydesc');
                      return (
	                    <button
	                      type='button'
	                      onClick={ _toggleCountySort }
	                      style={{
	                        background : 'none',
	                        border : 'none',
	                        padding : 0,
	                        font : 'inherit',
	                        fontWeight : 'inherit',
	                        cursor : 'pointer',
	                        whiteSpace : 'nowrap'
	                      }}
	                    >
	                      County <span style={{ color : countySortArrow.color }}>{ countySortArrow.icon }</span>
	                    </button>
                      );
                    })()}
                  </th>
	                  <th style={{ whiteSpace : 'nowrap' }}>
                    {(() => {
                      const createdOnSortArrow = _getSortArrow('createdonasc', 'createdondesc');
                      return (
	                    <button
	                      type='button'
	                      onClick={ _toggleSignedUpSort }
	                      style={{
                        background : 'none',
                        border : 'none',
                        padding : 0,
                        font : 'inherit',
                        fontWeight : 'inherit',
	                        cursor : 'pointer',
	                        whiteSpace : 'nowrap'
	                      }}
	                    >
	                      Signed-Up On <span style={{ color : createdOnSortArrow.color }}>{ createdOnSortArrow.icon }</span>
	                    </button>
                      );
                    })()}
	                  </th>
                  <th style={{ whiteSpace : 'nowrap' }}>Receipt Count</th>
                  <th style={{ whiteSpace : 'nowrap' }}>Line Item Count</th>
                  <th style={{ whiteSpace : 'nowrap' }}>Total €</th>
                  <th style={{ whiteSpace : 'nowrap' }}>Live Collector Cards</th>
                  <th style={{ whiteSpace : 'nowrap' }}>Closed Collector Cards</th>
                  <th style={{ whiteSpace : 'nowrap' }}>Total Stamps Ever</th>
                </tr>
              </thead>
              <tbody>
                { users.map((user, i) => {
                  console.log('USER: '+JSON.stringify(user));
                  let meridian_code = null;
                  if (user.hasOwnProperty('meridian_code') &&
                      user.meridian_code &&
                      user.meridian_code.trim() != '') {
                    meridian_code = user.meridian_code;
                  }
                  else {
                    if (user.hasOwnProperty('meridian_accountKey') &&
                        user.meridian_accountKey &&
                        user.meridian_accountKey.trim() != '') {
                      meridian_code = user.meridian_accountKey;
                    }                            
                    else {
                      meridian_code = '';
                    }
                  }

                  let meridian_firstname = null;
                  if (user.hasOwnProperty('meridian_firstName') &&
                      user.meridian_firstName &&
                      user.meridian_firstName.trim() != '') {
                    meridian_firstname = user.meridian_firstName;
                  }
                  else {
                    if (user.hasOwnProperty('meridian_firstname') &&
                        user.meridian_firstname &&
                        user.meridian_firstname.trim() != '') {
                      meridian_firstname = user.meridian_firstname;
                    }
                    else {
                      meridian_firstname = '';
                    }
                  }
                
                  const receiptCount = Number(user.additionalData?.receiptCount ?? 0);
                  const lineItemCount = Number(user.additionalData?.lineItemCount ?? 0);
                  const totalSpend = Number(user.additionalData?.totalSpend ?? 0);
                  
                  
                  return (
                    <tr
                      key={ user.email }
                      onClick={ () => { _handleUserRowClick(user.meridian_accountKey); } }
                      style={{ cursor : 'pointer' }}
                    >
                      <td>{ meridian_code }</td>
                      <td>{ meridian_firstname }</td>
                      <td>{ user.meridian_surname }</td>
                      <td>{ user.email }</td>
                      <td>{ user.meridian_county }</td>
                      <td>{ user.enrichedData.createdOn }</td>
                      { user.isAdditionalDataLoaded ? (
                        <>
                        <td style={{ textAlign : 'center' }}>{ receiptCount }</td>
                        <td style={{ textAlign : 'center' }}>{ lineItemCount }</td>
                        <td style={{ textAlign : 'left' }}>€{ totalSpend.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }</td>
                        <td style={{ textAlign : 'center', fontWeight : user.enrichedData.liveCollectorCardsCount > 0 ? 'bold' : null }}>{ user.enrichedData.liveCollectorCardsCount }</td>
                        <td style={{ textAlign : 'center', fontWeight : user.enrichedData.closedCollectorCardsCount > 0 ? 'bold' : null }}>{ user.enrichedData.closedCollectorCardsCount }</td>
                        <td style={{ textAlign : 'center', fontWeight : user.enrichedData.totalStampsEverCount > 0 ? 'bold' : null }}>{ user.enrichedData.totalStampsEverCount }</td>
                        </>
                      ) : (
                        <>
                        <td style={{ textAlign : 'center' }}>-</td>
                        <td style={{ textAlign : 'center' }}>-</td>
                        <td style={{ textAlign : 'center' }}>-</td>
                        <td style={{ textAlign : 'center' }}>-</td>
                        <td style={{ textAlign : 'center' }}>-</td>
                        <td style={{ textAlign : 'center' }}>-</td>
                        </>
                      )}

                    </tr>
                  );
                })}
              </tbody>
            </table>
            { isMoreUsersToLoad ? (
              <div style={{ paddingBottom : 20 }}>
                <Refreshing message='Loading more users...' />
              </div>
            ) : null }
            { nextPageToken != null ? (
              <div ref={ loadMoreTriggerRef } style={{ height : 1 }} />
            ) : null }
          </div>
        ) : null }
      </div>
    </>
  );

};

export default Users;

                  
                
        
                  

                
                  

            

        

  
  

  
